import React, { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '../../types';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';
import { SETTINGS_SECTIONS, settingsPath, DEFAULT_SETTINGS_SECTION } from '../../routes';
import { NAVBAR_HEIGHT } from './Navbar';
import useNavItems from './useNavItems';
import type { NavItem } from './useNavItems';
import { Icon, iconFor } from '../ui';

// The "Left sidebar" navigation layout: every module down the left, grouped
// by category, with Settings and its sections at the bottom. Collapses to an
// icon rail (remembered per browser); on a narrow screen it hides and the
// ☰ button in the top bar slides it in as a drawer — see .app-sidebar in
// index.css for that part, since inline styles cannot carry a media query.

const EXPANDED_WIDTH = 250;
const RAIL_WIDTH = 64;
const COLLAPSED_KEY = 'church_erp_sidebar';

const readCollapsed = (): boolean => {
  try { return localStorage.getItem(COLLAPSED_KEY) === 'collapsed'; } catch { return false; }
};

export interface SidebarProps {
  user: AuthUser | null;
  /** The drawer state on a narrow screen; ignored on a wide one. */
  mobileOpen?: boolean;
  onClose?: () => void;
}

const Sidebar = ({ user, mobileOpen = false, onClose }: SidebarProps) => {
  const { isDarkMode } = useTheme();
  const t = getTokens(isDarkMode);
  const navigate = useNavigate();
  const nav = useNavItems(user);

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? 'collapsed' : 'expanded'); } catch { /* private browsing */ }
  }, [collapsed]);

  // The drawer is always full width, so labels show there even when the
  // desktop rail is collapsed.
  const showLabels = !collapsed || mobileOpen;

  const go = useCallback((path: string) => {
    navigate(path);
    onClose?.();
  }, [navigate, onClose]);

  // Escape closes the drawer, as it does for any overlay.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen, onClose]);

  const itemStyle = (active: boolean, sub = false): CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
    padding: sub ? (showLabels ? '7px 12px 7px 46px' : '5px 12px') : '9px 12px',
    justifyContent: showLabels ? 'flex-start' : 'center',
    border: 'none', borderRadius: t.radius.lg, cursor: 'pointer', textAlign: 'left',
    borderLeft: `3px solid ${active ? t.primary : 'transparent'}`,
    backgroundColor: active ? t.primarySoft : 'transparent',
    color: active ? t.textAccent : t.textMain,
    fontWeight: active ? t.fontWeight.bold : t.fontWeight.normal,
    fontSize: sub ? t.fontSize.sm : t.fontSize.md,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    transition: 'background-color 0.15s ease'
  });

  const renderItem = (item: NavItem, active: boolean) => (
    <button
      key={item.id}
      type="button"
      onClick={() => go(item.path)}
      aria-current={active ? 'page' : undefined}
      title={showLabels ? undefined : item.title}
      style={itemStyle(active)}
    >
      <span style={{ display: 'inline-flex', width: '22px', justifyContent: 'center', flexShrink: 0, color: active ? t.primaryText : t.textMuted }} aria-hidden="true"><Icon name={iconFor(item.icon)} size={18} /></span>
      {showLabels && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>}
    </button>
  );

  const settingsActive = nav.settings != null && nav.activeModuleId === nav.settings.id;

  return (
    <>
      {mobileOpen && <div className="app-sidebar-backdrop" onClick={onClose} aria-hidden="true" />}

      <aside
        className={`app-sidebar${mobileOpen ? ' open' : ''}`}
        aria-label="Main menu"
        style={{
          width: collapsed ? RAIL_WIDTH : EXPANDED_WIDTH,
          flexShrink: 0, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column',
          position: 'sticky', top: NAVBAR_HEIGHT, height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
          backgroundColor: t.bgMain, borderRight: `1px solid ${t.border}`,
          transition: 'width 0.2s ease'
        }}
      >
        <div style={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 8px' }}>
          {renderItem(
            { id: 'home', route: '', icon: '🏠', title: 'Dashboard', path: '/' },
            nav.atHome
          )}

          {nav.groups.map((group) => (
            <div key={group.category} style={{ marginTop: '10px' }}>
              {showLabels ? (
                <div style={{
                  padding: '8px 12px 4px', color: t.textFaint, fontSize: t.fontSize.xs,
                  fontWeight: t.fontWeight.bold, letterSpacing: '0.8px', textTransform: 'uppercase'
                }}>
                  {group.category}
                </div>
              ) : (
                <div style={{ height: '1px', backgroundColor: t.border, margin: '6px 10px' }} aria-hidden="true" />
              )}
              {group.items.map((item) => renderItem(item, nav.activeModuleId === item.id))}
            </div>
          ))}

          {nav.settings && (
            <div style={{ marginTop: '10px', borderTop: `1px solid ${t.border}`, paddingTop: '10px' }}>
              {renderItem(nav.settings, settingsActive)}
              {/* The sections, so Settings needs no menu of its own. In the
                  collapsed rail they are icons with a tooltip. */}
              {settingsActive && SETTINGS_SECTIONS.map((section) => {
                const active = (nav.activeSection || DEFAULT_SETTINGS_SECTION) === section.key;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => go(settingsPath(section.key))}
                    aria-current={active ? 'page' : undefined}
                    title={showLabels ? undefined : section.label}
                    style={itemStyle(active, true)}
                  >
                    <span aria-hidden="true" style={{ display: 'inline-flex', color: t.textMuted }}><Icon name={iconFor(section.icon, 'settings')} size={16} /></span>
                    {showLabels && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.label}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className="app-sidebar-collapse"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          style={{
            ...itemStyle(false), borderTop: `1px solid ${t.border}`, borderRadius: 0,
            color: t.textMuted, fontSize: t.fontSize.sm, padding: '10px 12px'
          }}
        >
          <span style={{ fontSize: '16px', width: '22px', textAlign: 'center', flexShrink: 0 }} aria-hidden="true">
            {collapsed ? '»' : '«'}
          </span>
          {showLabels && <span>Collapse</span>}
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
